import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { P, Grid, Wrapper } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'
import { dayjsLondon, dayjsUtc } from 'lib/dayjs'
import lowerCase from 'lodash/lowerCase'
import { LAW_FIRM_NAMES } from 'utils/enums'

import {
  PRODUCTS,
  FarewillProduct,
  PROBATE_CASE_STATUSES,
  FUNERAL_PLANS_STATUSES,
} from 'utils/enums'
import { formatToHuman } from 'utils/helpers'
import { Task } from 'lib/models/task'
import { ProbateCaseAttributes } from 'lib/models/probate-case'
import { FuneralAttributes } from 'lib/models/funeral'
import { FuneralPlanAttributes } from 'lib/models/funeral-plan'
import { WillCaseAttributes } from 'lib/models/will-case'
import { LpaCaseAttributes } from 'lib/models/lpa-case'

import { Uppercase } from 'components/styled/uppercase'
import StatusBadge from 'components/status-badge'
import { WithCopyToClipboard } from 'components/with-copy-to-clipboard'

import CustomerDetails from './customer-details'
import {
  ProbateExternalLinks,
  FuneralExternalLinks,
  WillExternalLinks,
} from './external-links'

export type Attributes =
  | ProbateCaseAttributes
  | FuneralAttributes
  | FuneralPlanAttributes
  | WillCaseAttributes
  | LpaCaseAttributes

const StyledWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  margin-bottom: ${GTR.XS};
`

const StyledUppercase = styled(Uppercase)`
  margin: 0 ${GTR.XXS} 0 0;
`

const getDaysFromToday = (date?: string | Date | null) => {
  const daysAgo = dayjsUtc().diff(dayjsUtc(date), 'day')

  if (daysAgo === 1) return '1 day ago'
  return `${daysAgo} days ago`
}

type ProductHeaderProps = {
  attributes: Attributes
  productType: FarewillProduct
  caseId?: number
  showDeceased?: boolean
  showExternalLinks?: boolean
  showLastActivityAt?: boolean
  showLastCallAt?: boolean
  tasks?: Task[]
}

const ProductHeader = ({
  attributes,
  productType,
  caseId,
  showDeceased,
  showExternalLinks,
  showLastActivityAt,
  showLastCallAt,
  tasks,
}: ProductHeaderProps): React.ReactElement => {
  const lastActivityAt =
    'lastActivityAt' in attributes ? attributes.lastActivityAt : undefined
  const lastCallAt = 'lastCallAt' in attributes && attributes.lastCallAt
  const status = 'status' in attributes && attributes.status
  const reference = 'reference' in attributes ? attributes.reference : undefined
  const caseOwner =
    ('currentOwner' in attributes && attributes.currentOwner?.name) ||
    'No owner'
  const caseSupervisorName =
    ('supervisor' in attributes && attributes.supervisor?.name) || undefined

  const isProbate = productType === PRODUCTS.PROBATE
  const isFuneral = productType === PRODUCTS.FUNERAL
  const isFuneralPlan = productType === PRODUCTS.FUNERAL_PLAN
  const isWill = productType === PRODUCTS.WILL
  const lastActivity = `${getDaysFromToday(lastActivityAt)} (${dayjsLondon(
    lastActivityAt
  ).format('D MMM YYYY')})`

  const lastCall = lastCallAt
    ? `${getDaysFromToday(lastCallAt)} (${dayjsLondon(lastCallAt).format(
        'D MMM YYYY'
      )})`
    : 'No recent contact'

  const caseStatus = status
    ? isProbate
      ? PROBATE_CASE_STATUSES[status as keyof typeof PROBATE_CASE_STATUSES]
      : isFuneralPlan
        ? FUNERAL_PLANS_STATUSES[status as keyof typeof FUNERAL_PLANS_STATUSES]
        : lowerCase(status || '')
    : undefined

  const referredTo =
    isProbate && 'referredTo' in attributes && attributes.referredTo
      ? LAW_FIRM_NAMES[attributes.referredTo as keyof typeof LAW_FIRM_NAMES]
      : 'Unknown'

  return (
    <Grid>
      <Grid.Item span={7}>
        <StyledWrapper>
          <StyledUppercase>
            Customer &bull; {formatToHuman(productType)} &bull;{' '}
            {reference ? (
              <WithCopyToClipboard value={reference} label="Reference">
                {reference}
              </WithCopyToClipboard>
            ) : null}{' '}
            &bull;
          </StyledUppercase>
          {caseStatus && <StatusBadge status={caseStatus} />}
        </StyledWrapper>
        <CustomerDetails attributes={attributes} showDeceased={showDeceased} />
        {showExternalLinks && isProbate && (
          <ProbateExternalLinks
            attributes={attributes as ProbateCaseAttributes}
          />
        )}
        {showExternalLinks && isFuneral && (
          <FuneralExternalLinks
            attributes={attributes as FuneralAttributes}
            tasks={tasks}
          />
        )}
        {showExternalLinks && isWill && (
          <WillExternalLinks attributes={attributes as WillCaseAttributes} />
        )}
        {isProbate && (
          <>
            <P size="S" margin={0}>
              Referred to: {referredTo}
            </P>
            <P size="S" margin={0}>
              Case Supervisor:{' '}
              {caseSupervisorName || (
                <Link to={`/probate/cases/${caseId}#case-supervisor`}>
                  <strong>Unassigned</strong>
                </Link>
              )}
            </P>
            <P size="S" margin={0}>
              Case Owner: {caseOwner}
            </P>
          </>
        )}
        {showLastActivityAt && (
          <P size="S" margin={0}>
            Last activity: {lastActivity}
          </P>
        )}
        {showLastCallAt && (
          <P size="S" margin={['XS', 0, 0]}>
            Last contact: {lastCall}
          </P>
        )}
      </Grid.Item>
    </Grid>
  )
}

ProductHeader.propTypes = {
  attributes: PropTypes.object.isRequired,
  productType: PropTypes.string.isRequired,
  showExternalLinks: PropTypes.bool,
  showLastActivityAt: PropTypes.bool,
  showLastCallAt: PropTypes.bool,
}

ProductHeader.defaultProps = {
  showExternalLinks: true,
  showLastActivityAt: true,
  showLastCallAt: false,
}

export default ProductHeader
